import React from "react";
import './EditArtistRelations.scss';
import { Box, Button, Select, Stack } from "@mui/material";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { getArtist, getGenres, postArtistGenres } from "../../components/apis/music-api";
import ButtonBar from "../../components/button-bar/ButtonBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import { Genre } from "../../types/Genre";
import callApi from "../../components/call-api/call-api";
import { Artist } from "../../types/Artist";

type EditArtistGenresProps = {
    letter: string,
    artistName: string,
}

const EditArtistGenres = () => {
    const params = useParams<EditArtistGenresProps>();
    const navigate = useNavigate();
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ artist, setArtist ] = useState<Artist>();
    const [ all, setAll ] = useState<readonly Genre[]>([]);
    const [ left, setLeft ] = useState<readonly string[]>([]);
    const [ right, setRight ] = useState<readonly string[]>([]);
    const [ selectedLeft, setSelectedLeft ] = useState<string[]>([]);
    const [ selectedRight, setSelectedRight ] = useState<string[]>([]);
    
    useEffect(() => {
        callApi<Genre[]>(getGenres(getAccessToken()), setAll);
        callApi<Artist>(getArtist(getAccessToken(), params.artistName ?? ''), setArtist);
    }, [params, getAccessToken]);

    useEffect(() => {
        if (artist !== undefined && all !== undefined) {
            const genres = artist?.genres?.map(x => x.name) ?? [];
            setLeft(not(all.map(x => x.name), genres));
            setRight(genres);
        }
    }, [artist, all]);
    
    const not = (a: readonly string[], b: readonly string[]) => {
        return a.filter((value) => !b.includes(value));
    }

    const handleLeftToRight = () => {
        setRight(right.concat(selectedLeft));
        setLeft(not(left, selectedLeft));
    };

    const handleRightToLeft = () => {
        setLeft(left.concat(selectedRight));
        setRight(not(right, selectedRight));
    };

    const handleChangeMultipleLeft = (event: ChangeEvent<HTMLSelectElement>) => {
        const { options } = event.target;
        const value: string[] = [];
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                value.push(options[i].value);
            }
        }

        setSelectedLeft(value);
    };

    const handleChangeMultipleRight = (event: ChangeEvent<HTMLSelectElement>, ) => {
        const { options } = event.target;
        const value: string[] = [];
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                value.push(options[i].value);
            }
        }

        setSelectedRight(value);
    };

    const save = async () => {
        const ids = all.filter(x => right.includes(x.name))?.map(x => x.id);
        await postArtistGenres(getAccessToken(), params.artistName ?? '', ids);
        navigate(`/letters/${params.letter}/artists/${params.artistName}`);
    }
    
    const handleSave = () => {
        if (params.letter !== undefined && params.artistName !== undefined) {
            save();
        }
    }
    
    return (
        <>
            <ButtonBar
                leftButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${params.artistName}`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faArrowLeft} className="icon"/>
                        Back To Artist
                    </Button>
                ]}
                rightButtons={[
                    <Button variant="contained" size="small" onClick={handleSave}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faFloppyDisk} className="icon"/>
                        Save Changes
                    </Button>
                ]}
            />
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Box>
                    <Select<string[]>
                        multiple
                        native
                        //value={selectedLeft}
                        // @ts-ignore Typings are not considering `native`
                        onChange={handleChangeMultipleLeft}
                        label="All Artists"
                        inputProps={{ id: 'select-multiple-native' }}
                        SelectDisplayProps={{ style: { minHeight: '320px' } }}
                        sx={{ minHeight: '320px', width: '320px' }}
                    >
                        {left.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </Select>
                </Box>
                <Box>
                    <Stack direction="column" sx={{ alignItems: 'center' }}>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleLeftToRight}
                            // disabled={leftChecked.length === 0}
                            aria-label="move selected right"
                        >
                            &gt;
                        </Button>
                        <Button
                            sx={{ my: 0.5 }}
                            variant="outlined"
                            size="small"
                            onClick={handleRightToLeft}
                            // disabled={rightChecked.length === 0}
                            aria-label="move selected left"
                        >
                            &lt;
                        </Button>
                    </Stack>
                </Box>
                <Box>
                    <Select<string[]>
                        multiple
                        native
                        //value={selectedRight}
                        // @ts-ignore Typings are not considering `native`
                        onChange={handleChangeMultipleRight}
                        label="All Artists"
                        inputProps={{ id: 'select-multiple-native' }}
                        sx={{ width: '320px' }}
                    >
                        {right.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </Select>
                </Box>
            </Stack>
        </>
    );
}

export default EditArtistGenres;